import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { PopUpType } from '../../../common/components/PopUp';
import { useApi } from '../../../api/useApi';
import { useProtocol } from '../context';
import { ComplementType, RequestsFormValues } from './models';
import { useReferences } from '../../../helpers/context_manager/useReferences';
import {
  ApiComplementType,
  API_COMPLEMENT_TYPES,
  API_KEY_MAP,
  TYPE_OPTIONS,
  openPdfBlob,
  mapDataToFormValues
} from '../shared/labRequestTypes';

type ComplementOption = {
  type: ComplementType;
  label: string;
};

const COMPLEMENT_OPTIONS: ComplementOption[] = TYPE_OPTIONS.map((o) => ({
  type: o.value as ComplementType,
  label: o.label
}));

type RequestIds = Record<ApiComplementType, number | null>;

function hasAnyBooleanTrue(obj: unknown): boolean {
  if (typeof obj === 'boolean') return obj;
  if (typeof obj !== 'object' || obj === null) return false;
  return Object.values(obj as Record<string, unknown>).some(hasAnyBooleanTrue);
}

function hasNrlRespiratoryValidSelection(fd: any): boolean {
  const matFilled = Object.values(fd.material).some(
    (item: any) => item?.taken_at?.trim() || item?.note?.trim() || item?.text?.trim()
  );
  const examChecked = Object.values(fd.examination).some(
    (item: any) => item?.pcr || item?.confirmation || item?.nrl_seq_lt28
  );
  return matFilled || examChecked;
}

const makeSymptom = () => ({ present: false, days: null });
const makeSymptomWithTemp = () => ({ present: false, days: null, value_c: null });
const makeSymptomOther = () => ({ present: false, days: null, text: null });
const makeYesNo = () => ({ enabled: false, disabled: false });
const makeExamItem = () => ({ pcr: false, confirmation: false, nrl_seq_lt28: false });
const makeDatedMaterial = () => ({ taken_at: null, note: null });

const defaultNrlRespiratoryFormData = (): RequestsFormValues['nrl_respiratory']['form_data'] => ({
  symptoms: {
    temperature: makeSymptomWithTemp(),
    malaise: makeSymptom(),
    runny_nose: makeSymptom(),
    cough: makeSymptom(),
    dyspnea: makeSymptom(),
    headache: makeSymptom(),
    chest_pain: makeSymptom(),
    abdominal_pain: makeSymptom(),
    muscle_pain: makeSymptom(),
    vomiting: makeSymptom(),
    diarrhea: makeSymptom(),
    tonsillitis: makeSymptom(),
    conjunctivitis: makeSymptom(),
    tracheitis: makeSymptom(),
    bronchitis: makeSymptom(),
    exanthem: makeSymptom(),
    loss_smell_taste: makeSymptom(),
    other: makeSymptomOther()
  },
  sari: {
    treatment: { enabled: false, disabled: false, from_date: null, text: null },
    atb: makeYesNo(),
    antivirals: makeYesNo(),
    obesity: makeYesNo(),
    respiratory_support: makeYesNo(),
    ecmo: makeYesNo(),
    pregnancy: { enabled: false, disabled: false, text: null },
    flu_vaccination: makeYesNo(),
    vaccine_name_text: null,
    vaccine_batch_text: null
  },
  examination: {
    influenza: makeExamItem(),
    sarscov2: makeExamItem(),
    rsv_metapneumo: makeExamItem(),
    parainfl_corona: makeExamItem(),
    rhino_entero: makeExamItem(),
    adeno_boca: makeExamItem(),
    parecho: makeExamItem(),
    mers: makeExamItem(),
    chlam_mycopl: makeExamItem(),
    legionella: makeExamItem(),
    other: { pcr: false, confirmation: false, nrl_seq_lt28: false, text: null }
  },
  material: {
    swab_nose_throat: makeDatedMaterial(),
    endotracheal_swab: makeDatedMaterial(),
    bal: makeDatedMaterial(),
    sputum_or_aspirate: makeDatedMaterial(),
    csf: makeDatedMaterial(),
    biopsy: makeDatedMaterial(),
    full_blood_clot: makeDatedMaterial(),
    saliva: makeDatedMaterial(),
    other: { taken_at: null, note: null, text: null }
  }
});

const defaultHistologyFormData = (): RequestsFormValues['histology']['form_data'] => ({
  histology_fixed: {
    cns: { enabled: false, choices: [] },
    nasal_septum: false,
    epiglottis: false,
    larynx: false,
    thyroid_gland: false,
    trachea: false,
    lungs: false,
    heart: false,
    thymus: false,
    liver: false,
    spleen: false,
    stomach: false,
    small_intestine: false,
    large_intestine_colon: false,
    pancreas: false,
    adrenal_glands: false,
    kidneys: false,
    prostate: false,
    eyeballs_and_optic_nerves: false,
    pituitary_gland: false,
    bone_marrow: { enabled: false, choices: [] },
    other: []
  },
  special_staining: [],
  frozen_material: [],
  histology_notes: {
    sectioning_physician: { enabled: true, text: '' },
    whole_organ_fixation: null,
    other: null
  },
  cytology: {
    nasal_cavity: { enabled: false, choices: [] },
    oral_cavity: false,
    vulva: false,
    vagina: false,
    uterus: { enabled: false, choices: [] },
    rectum: false,
    vitreous: { enabled: false, choices: [] },
    other: [],
    note: null
  }
});

const defaultNrlOther = (): RequestsFormValues['nrl_other'] => ({
  swab_nose_throat_collected_at: null,
  swab_nose_throat_note: '',
  endotracheal_swab_aspirate_sputum_collected_at: null,
  endotracheal_swab_aspirate_sputum_note: '',
  bal_collected_at: null,
  bal_note: '',
  csf_collected_at: null,
  csf_note: '',
  biopsy_collected_at: null,
  biopsy_note: '',
  full_blood_clot_collected_at: null,
  full_blood_clot_note: '',
  saliva_collected_at: null,
  saliva_note: '',
  autopsy_swabs_collected_at: null,
  autopsy_swabs_note: '',
  other_material_name: '',
  other_collected_at: null,
  other_note: ''
});

export const useRequests = () => {
  const api = useApi();
  const { protocol } = useProtocol();
  const { references } = useReferences();

  const [message, setMessage] = useState<{ type: PopUpType; message: ReactNode } | undefined>(
    undefined
  );
  const [savedComplements, setSavedComplements] = useState<Set<string>>(new Set());
  const [dirtyComplements, setDirtyComplements] = useState<Set<string>>(new Set());
  const [currentUser, setCurrentUser] = useState<{ id: number; name: string } | null>(null);
  const [activeComplementType, setActiveComplementType] = useState<ApiComplementType | null>(null);
  const [isBatchLoading, setIsBatchLoading] = useState(true);

  useEffect(() => {
    api.user
      .getUser()
      .then((res) => {
        const { id, first_name, last_name, username } = res.data;
        const name = first_name || last_name ? `${first_name} ${last_name}`.trim() : username;
        setCurrentUser({ id, name });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isLoadingRef = useRef(false);
  const batchLoadedRef = useRef(false);

  const requestIdsRef = useRef<RequestIds>({
    ct: null,
    rtg: null,
    toxicology: null,
    tryptase: null,
    microbiology: null,
    genetics: null,
    biochemistry: null,
    histology: null,
    nrl_other: null,
    nrl_respiratory: null,
    transport: null,
    virology: null
  });

  const methods = useForm<RequestsFormValues>({
    defaultValues: {
      examinationType: '',
      fnbToxicologist: '',
      selectedComplement: null,
      otherComplementNote: '',
      ct: { request_text: null },
      rtg: { request_text: null },
      toxicology: {
        intoxication_suspicion: 'NE',
        circumstances_note: '',
        note: '',
        form_data: {
          mechanism_of_death: {
            sudden_death: false,
            suicide: false,
            traffic_accident: false,
            murder: false,
            work_injury: false,
            other_injury: false,
            other: false
          },
          body_changes: {
            advanced_autolysis: false,
            putrefaction: false,
            burn: false,
            other: false
          },
          material: {
            femoral_blood: { enabled: false, amount: 0 },
            spleen_blood: { enabled: false, amount: 0 },
            heart_cavity_blood: { enabled: false, amount: 0 },
            subclavian_blood: { enabled: false, amount: 0 },
            bladder_wash: { enabled: false },
            other_objects: { enabled: false, text: null },
            organs: { enabled: false },
            vitreous: { enabled: false },
            stomach_content: { enabled: false },
            hair: { enabled: false },
            bile: { enabled: false },
            urine: { enabled: false, amount: null }
          },
          required_tests: {
            by_measures: false,
            ethanol: false,
            extractive: false,
            heavy_metals: false,
            cohb: false,
            just_sample: false,
            other: false,
            other_text: null
          }
        }
      },
      tryptase: {
        do_request: false
      },
      microbiology: {
        note: '',
        form_data: {
          pus_fluid_tissue: {
            pus: { enabled: false, localization: '' },
            abscess_cyst: { enabled: false, localization: '' },
            furuncle_carbuncle: false,
            effusion: { enabled: false, localization: '' },
            biopsy_tissue: { enabled: false, localization: '' },
            joint_puncture: false,
            ascites: false
          },
          git: {
            rectal_swab_enteric_pathogens: false,
            cholera_swab: false,
            rectal_swab_esbl_kpc_vre: false,
            bile: false,
            appendix_swab: false,
            clostridioides_difficile: false,
            stool_viruses: false,
            helicobacter_pylori: false
          },
          hcd: {
            cultivation_type: null,
            throat_tonsils_nasopharynx: false,
            nose: false,
            oral_mucosa_tongue_swab: false,
            paranasal_sinuses: false,
            peritonsillar_abscess: false,
            sars_cov2_antigen_nasopharynx: false
          },
          dcd: {
            cultivation_type: null,
            sputum: false,
            bal_aspirate: false,
            tracheostomy_swab: false,
            thoracic_puncture: false,
            legionella_antigen_urine: false,
            pneumococcus_antigen_urine: false,
            other: { enabled: false, text: '' }
          },
          urogenital_sti: {
            cultivation_type: null,
            vaginal_swab: false,
            rectal_swab: false,
            urethral_swab: false,
            throat_swab: false,
            cervical_swab: false,
            prostatic_secretion: false,
            vaginorectal_swab: false,
            mop_two_slides: false,
            gonorrhea_smear: false,
            trichomonas_smear: false,
            iud: false
          },
          lesion_sample: {
            anaerobic: false,
            leg_ulcer_swab: false,
            surgical_wound_swab: false,
            traumatic_wound_swab: false,
            fistula_swab: false,
            skin_swab: false,
            decubitus_swab: false
          },
          urine: { midstream: false, nephrostomy_epicystostomy: false, permanent_catheter: false },
          cns: { csf: false, streptococcus_pneumoniae_antigen: false, aspergillus_antigen: false, cryptococcus_antigen: false },
          ear: { side: null, external_auditory_canal_swab: false, middle_ear_content: false, other: { enabled: false, text: '' } },
          eye: { side: null, conjunctival_swab: false, other: { enabled: false, text: '' } },
          neonatology: { amniotic_fluid: false, ear_swab: false, umbilicus_swab: false, axilla_swab: false, gastric_content: false, other: { enabled: false, text: '' } },
          pathology: { liver: false, stomach: false, spleen: false, lung: false, small_intestine: false, lymph_node: false, large_intestine: false, other: { enabled: false, text: '' } },
          sterility: { environment_swab: false, fluid: false, other: { enabled: false, text: '' } },
          foreign_material: { drain: false, cannula: false, redon: false, vascular_catheter: false },
          tbc_cultivation: { enabled: false, material: '' },
          mrsa_screening: { throat: false, nose: false, urine: false, hairline: false, perineum: false, wound: false },
          bloodstream_bactec: {
            collection_source: null,
            other_sterile_text: '',
            bottle_counts: { aerobic: null, anaerobic: null, pediatric: null },
            extended_cultivation_hacek: false,
            sequencing: false,
            other_request: { enabled: false, text: '' }
          },
          mycology: {
            cultivation: { enabled: false, material: '' },
            panfungal_antigen_bd_glucan: false,
            galactomannan_aspergillus: false,
            cryptococcus_antigen: false,
            pcr_multiplex_dermatophyta: false
          },
          pcr: {
            herpetic: { hsv1_hsv2: false, hsv1_hsv2_vzv: false, cmv_quantitative: false, ebv_quantitative: false, hhv6_7: false, hhv8: false },
            respiratory: { sars_cov2_flu_rsv: false, sars_cov2_flu_rsv_gxp: false, multiplex_cap: false, multiplex_resp_panel: false, multiplex_pneumonia_plus: false, mycobacterium_tbc_complex: false, mycobacterium_tbc_complex_gxp: false, atypical_mycobacteria: false, strain_identification: false, aspergillus_spp: false, pneumocystis_jirovecii: false },
            pediatric_exanthematic: { parvovirus_b19: false, parotitis: false, morbilli: false },
            viral_hepatitides: { hbv_quantitative: false, hcv_quantitative: false, hcv_genotyping: false, hev: false },
            sti: { chlamydia_trachomatis: false, chlamydia_trachomatis_gxp: false, neisseria_gonorrhoeae: false, neisseria_gonorrhoeae_gxp: false, monkeypox: false, multiplex_panel_sti: false, multiplex_panel_gu: false },
            neuroinfections: { enteroviruses: false, borrelia: false, multiplex_meningitis_viruses: false, multiplex_meningitis_bacteria: false, multiplex_meningitis: false },
            joint_implants: { multiplex_joints: false },
            travel_infections: { multiplex_tropical_fevers: false, malaria: false, multiplex_protozoa: false, multiplex_helminths: false },
            other: { multiplex_tick_borne: false, multiplex_dermatophyta: false, pcr_tularemia: false }
          }
        }
      },
      genetics: {
        pcr: null,
        medical: null
      },
      biochemistry: {
        primary_sample: null,
        form_data: {
          blood_clotted: {
            natrium: false, kalium: false, chlorides: false, calcium_total: false,
            phosphorus: false, magnesium: false, glucose: false, urea: false,
            creatinine: false, uric_acid: false, bilirubin: false, bilirubin_conjugated: false,
            ast: false, alt: false, alp: false, ggt: false, ck: false, ldh: false,
            ches: false, ams: false, lipase: false, bile_acids: false, ace: false,
            osmolality: false, cystatin_c: false, crp: false,
            cholesterol: false, hdl_cholesterol: false, ldl_cholesterol: false,
            triacylglycerols: false, apolipoprotein_a1: false, apolipoprotein_b: false,
            lipoprotein_a: false, total_protein: false, albumin: false,
            beta_2_microglobulin: false, prealbumin: false, transferrin: false,
            transferrin_saturation: false, ferritin: false, iron: false,
            iron_binding_capacity: false, vitamin_b12: false, vitamin_b12_active: false,
            folate: false, hs_troponin_i: false, myoglobin: false, nt_probnp: false,
            digoxin: false, theophylline: false, afp: false, cea: false,
            ca_15_3: false, ca_19_9: false, ca_125: false, ca_72_4: false,
            hcg_beta: false, psa_total: false, psa_free: false, scca: false,
            s100b: false, nse: false, tsh: false, t4_total: false, t3_total: false,
            t4_free: false, t3_free: false, anti_tg: false, anti_tpo: false,
            anti_tsh_receptor_trak: false, cortisol: false, estradiol: false,
            progesterone: false, prolactin: false, lh: false, fsh: false,
            testosterone: false, dhea_s: false, shbg: false, c_peptide: false,
            vitamin_d_25_oh: false, phenytoin: false, valproic_acid: false,
            lithium: false, interleukin_6: false, procalcitonin_pct: false,
            plgf: false, igf_1: false
          },
          blood_unclotted: {
            blood_count: false, blood_count_diff_analyzer: false,
            blood_count_reticulocytes: false, blood_count_diff_reticulocytes: false,
            immature_platelet_fraction: false, differential_microscopy: false,
            platelets_microscopy: false, schistocytes: false
          },
          blood_k3edta: {
            hba1c: false, homocysteine: false, parathormone: false
          },
          blood_naf_edta: {
            glucose: false, ogtt: false, ogtt_pregnant: false, lactate: false
          },
          blood_citrate: {
            prothrombin_test_quick: false, aptt: false, thrombin_test: false,
            d_dimers: false, fibrinogen: false, antithrombin: false, fdp: false, fm: false,
            anticoagulation_treatment: '',
            protein_c: false, protein_s: false, apc_resistance: false,
            factor_viii: false, factor_ix: false, factor_xii: false,
            lupus_anticoagulans: false, anti_xa_lmwh: false, anti_xa_ufh: false,
            arixtra: false, apixaban: false, rivaroxaban: false,
            acetylsalicylic_acid_asa: false,
            clopidogrel_prasugrel_ticlopidine_ticagrelor: false
          },
          blood_clotted_separate: {
            blood_group_rh_factor: false, antibody_screening: false, direct_coombs_test: false
          },
          urine_single: {
            urine_chemistry_morphology: false, amylase: false, cb_creatinine: false,
            albumin_creatinine: false, osmolality: false, beta_2_microglobulin: false
          }
        }
      },
      histology: {
        requesting_physician: '',
        macroscopic_heart_histochemistry: false,
        form_data: defaultHistologyFormData()
      },
      nrl_other: defaultNrlOther(),
      nrl_respiratory: {
        important_symptoms_note: '',
        focus_note: '',
        form_data: defaultNrlRespiratoryFormData()
      },
      virology: {
        form_data: {
          bacterial_serology: {
            tularemia_antibodies: false,
            brucellosis_antibodies: false,
            toxoplasmosis_kfr_igg_igm_iga_ige: false,
            yersinia_species_igg_iga_wb: false,
            widal_salmonella_antibodies: false,
            borreliosis_igg_igm_elisa_wb: false,
            cxcl13_csf: false,
            antibody_index_csf_blood: false
          },
          mycology: {
            cryptococcus_antigenemia: false,
            aspergillus_antigenemia: false
          },
          mycobacteriology: {
            quantiferon_tb: false,
            t_spot_tb: false
          },
          congenital_infections: {
            toxoplasma_gondii_kfr_igm_iga_igg: false,
            rubella_virus: false,
            herpetic_viruses: false,
            parvovirus_b19: false
          },
          childhood_exanthem_infections: {
            rubella_virus_igm_igg: false,
            morbillivirus_igm_igg: false,
            parvovirus_b19_igm_igg: false,
            mumps_virus_igm_igg: false
          },
          traveler_infections: {
            chikungunya_virus_igm_igg: false,
            dengue_virus_igm_igg: false,
            dengue_virus_ns1_ag: false,
            zika_virus_igm_igg: false,
            hantaviruses_igm_igg_typing: false,
            japanese_encephalitis_virus_igm_igg: false,
            west_nile_virus_igm_igg: false
          },
          respiratory_infections: {
            respiratory_block: false,
            influenza_a_b_kfr_igm: false,
            adenoviruses_kfr_igm: false,
            rsv_kfr_igm_iga: false,
            mycoplasma_pneumoniae_kfr_igm_iga: false,
            parainfluenza_1_3_iga_igg: false,
            legionella_pneumophila_igm_igg: false,
            bordetella_pertussis_iga_igg: false,
            bordetella_parapertussis_iga_igg: false,
            sars_cov_2_antibodies_igm_iga: false,
            sars_cov_2_spike_protein_igg: false,
            sars_cov_2_n_antigen_blood: false,
            sars_cov_2_antigen_nasopharyngeal_swab: false
          },
          atypical_pneumonia: {
            mycoplasma_pneumoniae_kfr_igm_iga_igg: false,
            chlamydia_lps_igm_iga_igg: false,
            chlamydia_pneumoniae_psittaci_igm_iga_igg: false
          },
          neuroinfections: {
            tick_borne_meningoencephalitis_virus: false,
            enteroblock_kfr_igm_iga_igg: false,
            neuroborreliosis_cxcl13: false,
            neurosyphilis_cxcl13: false
          },
          herpetic_infections: {
            herpetic_block: false,
            hsv_1_2_igm_igg_kfr: false,
            cmv_igm_igg_igg_avidity_kfr: false,
            vzv_igm_igg_kfr: false,
            ebv_igm_igg_anti_ea_vca_ebna: false,
            hhv6_igm_igg: false
          },
          rickettsiae: {
            bartonella_igm_igg: false,
            rickettsia_conorii_igm_igg: false,
            coxiella_burnetii_igm_iga_igg_kfr: false
          },
          sexually_transmitted_infections: {
            syphilis_serology_rpr_tppa: false,
            hiv_1_2_p24_antigen: false,
            chlamydia_trachomatis_igm_iga_igg: false,
            mycoplasma_hominis_igm_igg: false,
            ureaplasma_urealyticum_igm_igg: false
          },
          viral_hepatitis: {
            hepatitis_panel_a_b_c: false,
            hepatitis_a: false,
            anti_hav_igm: false,
            anti_hav_ig_total: false,
            hepatitis_b_hbsag_quantitative: false,
            hepatitis_b_hbsag_confirmation: false,
            hepatitis_b_hbeag_quantitative: false,
            hepatitis_b_anti_hbe_igg: false,
            hepatitis_b_anti_hbs_quantitative: false,
            hepatitis_b_anti_hbc_ig_total: false,
            hepatitis_b_anti_hbc_igm: false,
            hepatitis_c: false,
            anti_hcv_ig_total: false,
            anti_hcv_confirmation_wb: false,
            hepatitis_e: false,
            anti_hev_igg_igm: false,
            anti_hev_confirmation_wb: false
          },
          post_vaccination_igg_check: {
            tick_borne_encephalitis_virus_quantitative: false,
            poliovirus_1_2_3: false,
            vzv: false,
            rubella_virus_quantitative: false,
            morbillivirus: false,
            mumps_virus: false,
            tetanus: false,
            hepatitis_b: false,
            sars_cov_2_spike_protein: false
          },
          direct_antigen_detection: {
            respiratory_infections: {
              direct_sars_cov_2_antigen_nasopharyngeal_swab: false
            },
            git_infections_stool: {
              rotaviruses: false,
              adenoviruses: false,
              astroviruses: false,
              noroviruses_genotype_1_2: false,
              enteroviruses: false
            }
          }
        }
      },
      transport: {
        requesting_department: '',
        form_data: {
          reason: { text: 'Převoz těla z pitvy' },
          origin: {
            use_provider_department: true,
            name_override: '',
            address_override: '',
            paid_by_insurance: false
          },
        }
      }
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  // Auto-select first active examination type so getCurrent always has one to pass
  useEffect(() => {
    const first = references.examinationTypes.find((et) => et.is_active);
    if (first && !methods.getValues('examinationType')) {
      methods.setValue('examinationType', String(first.id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [references.examinationTypes]);

  // ---------------------------------------------------------------------------
  // Load-on-mount: fetch existing requests for this protocol
  // ---------------------------------------------------------------------------

  const setFormValuesFromDraft = useCallback((type: ApiComplementType, r: any) => {
    const values = mapDataToFormValues(type, r);
    const typeData = values[type as keyof typeof values];
    if (typeData !== undefined) {
      methods.setValue(type as any, typeData as any);
    }

    // Protocol-specific defaults — závislé na protocol kontexte
    if (type === 'histology') {
      if (!r.requesting_physician && protocol?.pathologist1) {
        methods.setValue('histology.requesting_physician', String(protocol.pathologist1));
      }
      if (!r.form_data?.histology_notes?.sectioning_physician?.text && protocol?.pathologist1_name) {
        methods.setValue(
          'histology.form_data.histology_notes.sectioning_physician.text' as any,
          protocol.pathologist1_name
        );
      }
    }
    if (type === 'transport' && r.form_data && !r.form_data.reason?.text) {
      methods.setValue('transport.form_data.reason.text' as any, 'Převoz těla z pitvy');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocol?.pathologist1, protocol?.pathologist1_name]);

  // Batch-load všetkých typov žádaniek pri mount / keď je dostupný protokol
  useEffect(() => {
    if (!protocol?.id || batchLoadedRef.current) return;

    const examinationTypeId = parseInt(methods.getValues('examinationType'), 10);
    if (!examinationTypeId || isNaN(examinationTypeId)) return;

    batchLoadedRef.current = true;

    const batchLoad = async () => {
      isLoadingRef.current = true;
      try {
        const results = await Promise.allSettled(
          API_COMPLEMENT_TYPES.map((type) =>
            (api.labRequests[API_KEY_MAP[type] as keyof typeof api.labRequests] as any)
              .getCurrent(protocol.id, examinationTypeId)
              .then((draft: { data: any }) => ({ type, draft }))
          )
        );

        const newSaved = new Set<string>();

        for (const result of results) {
          if (result.status === 'fulfilled') {
            const { type, draft } = result.value as { type: ApiComplementType; draft: { data: any } };
            requestIdsRef.current[type] = draft.data.id;
            setFormValuesFromDraft(type, draft.data);

            if (draft.data.examination_type) {
              methods.setValue('examinationType', String(draft.data.examination_type));
            }
            if (type === 'toxicology' && draft.data.toxicologist) {
              methods.setValue('fnbToxicologist', String(draft.data.toxicologist));
            }

            if (draft.data.updated === true) {
              newSaved.add(type);
            }
          }
        }

        setSavedComplements(newSaved);
      } finally {
        isLoadingRef.current = false;
        setDirtyComplements(new Set());
        setIsBatchLoading(false);
      }
    };

    batchLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocol?.id, protocol?.pathologist1, protocol?.pathologist1_name]);

  // Load draft when user selects a complement type
  useEffect(() => {
    if (!activeComplementType || !protocol?.id) return;
    if (requestIdsRef.current[activeComplementType] !== null) return;

    const loadDraft = async () => {
      isLoadingRef.current = true;
      const examinationTypeId = parseInt(methods.getValues('examinationType'), 10);
      if (!examinationTypeId || isNaN(examinationTypeId)) {
        isLoadingRef.current = false;
        setMessage({ type: 'error', message: 'Nejprve vyberte typ vyšetření.' });
        return;
      }
      try {
        const draft = await (api.labRequests[API_KEY_MAP[activeComplementType] as keyof typeof api.labRequests] as any)
          .getCurrent(protocol.id, examinationTypeId);
        requestIdsRef.current[activeComplementType] = draft.data.id;
        setFormValuesFromDraft(activeComplementType, draft.data);
        if (draft.data.examination_type) {
          methods.setValue('examinationType', String(draft.data.examination_type));
        }
        if (activeComplementType === 'toxicology' && draft.data.toxicologist) {
          methods.setValue('fnbToxicologist', String(draft.data.toxicologist));
        }
      } catch (error: any) {
        setMessage({
          type: 'error',
          message: `Chyba při načítání žádanky: ${extractErrorMessage(error, 'Neznámá chyba.')}`
        });
      } finally {
        isLoadingRef.current = false;
        setDirtyComplements(new Set());
      }
    };

    loadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeComplementType, protocol?.id]);

  useEffect(() => {
    const subscription = methods.watch((data, { name }) => {
      if (isLoadingRef.current || !name) return;

      if (name === 'selectedComplement') {
        const newType = data.selectedComplement;
        if (newType && (API_COMPLEMENT_TYPES as readonly string[]).includes(newType as string)) {
          setActiveComplementType(newType as ApiComplementType);
        }
        return;
      }

      const complement = API_COMPLEMENT_TYPES.find((t) => (name as string).startsWith(t));
      if (complement) {
        setDirtyComplements((prev) => {
          if (prev.has(complement)) return prev;
          return new Set([...prev, complement]);
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  // ---------------------------------------------------------------------------
  // Submit: save the single selected complement type
  // ---------------------------------------------------------------------------

const extractErrorMessage = (error: any, fallback: string): string => {
  const MAX_LENGTH = 500;

  const getAllStrings = (obj: any): string[] => {
    if (typeof obj === 'string') return [obj];
    if (Array.isArray(obj)) return obj.flatMap(getAllStrings);
    if (typeof obj === 'object' && obj !== null) {
      return Object.values(obj).flatMap(getAllStrings);
    }
    return [];
  };

  let finalMessage = fallback;

  if (error?.response?.data) {
    const errorData = error.response.data;
    if (typeof errorData === 'string') {
      finalMessage = errorData;
    } else if (typeof errorData === 'object' && errorData !== null) {
      const messages = getAllStrings(errorData);
      if (messages.length > 0) {
        finalMessage = Array.from(new Set(messages)).join('\n');
      }
    }
  } else if (error?.message && typeof error.message === 'string' && error.message.trim()) {
    finalMessage = error.message;
  }

  return finalMessage.length > MAX_LENGTH 
    ? finalMessage.substring(0, MAX_LENGTH) + '...' 
    : finalMessage;
};

  const onSubmit: SubmitHandler<RequestsFormValues> = async (data) => {
    if (!protocol?.id) {
      setMessage({ type: 'error', message: 'Chyba: ID protokolu nebylo nalezeno.' });
      return;
    }

    const type = data.selectedComplement;
    if (!type) {
      setMessage({ type: 'error', message: 'Vyberte typ žádanky.' });
      return;
    }

    if (!(API_COMPLEMENT_TYPES as readonly string[]).includes(type)) {
      setMessage({ type: 'error', message: 'Tento typ žádanky zatím není implementován.' });
      return;
    }

    const examinationTypeId = parseInt(data.examinationType, 10);
    if (!data.examinationType || isNaN(examinationTypeId)) {
      setMessage({ type: 'error', message: 'Vyberte typ vyšetření.' });
      return;
    }

    const autopsyProtocolId = protocol.id;
    const toxicologistId = data.fnbToxicologist ? parseInt(data.fnbToxicologist, 10) || null : null;

    const baseFields = {
      autopsy_protocol: autopsyProtocolId,
      examination_type: examinationTypeId,
      created_by: null,
      updated: true
    };

    const apiType = type as ApiComplementType;
    const existingId = requestIdsRef.current[apiType];

    if (existingId === null) {
      setMessage({ type: 'error', message: 'Chyba: Žádanka nebyla inicializována. Zkuste obnovit stránku.' });
      return;
    }

    try {
      if (apiType === 'ct') {
        const payload = { ...baseFields, request_text: data.ct.request_text };
        await api.labRequests.ct.patch(existingId, payload);
      } else if (apiType === 'rtg') {
        const payload = { ...baseFields, request_text: data.rtg.request_text };
        await api.labRequests.rtg.patch(existingId, payload);
      } else if (apiType === 'toxicology') {
        const amountMaterials: { key: 'femoral_blood' | 'spleen_blood' | 'heart_cavity_blood' | 'subclavian_blood' | 'urine'; label: string }[] = [
          { key: 'femoral_blood', label: 'krev femorální' },
          { key: 'spleen_blood', label: 'krev splavy' },
          { key: 'heart_cavity_blood', label: 'krev ze srdečních dutin' },
          { key: 'subclavian_blood', label: 'krev podklíčová' },
          { key: 'urine', label: 'moč' }
        ];
        for (const { key, label } of amountMaterials) {
          const mat = data.toxicology.form_data.material[key];
          if (!mat.enabled && mat.amount !== null && Number(mat.amount) >= 1) {
            setMessage({ type: 'error', message: `Toxikologie: Zadali jste množství pro „${label}", ale příslušný checkbox není zaškrtnut.` });
            return;
          }
        }
        const anyMaterialEnabled = Object.values(data.toxicology.form_data.material).some(
          (m: any) => m?.enabled === true
        );
        if (!anyMaterialEnabled) {
          setMessage({ type: 'error', message: 'Toxikologie: Vyberte alespoň jeden materiál.' });
          return;
        }
        const anyTestRequired = (Object.entries(data.toxicology.form_data.required_tests) as [string, unknown][])
          .some(([key, value]) => key !== 'other_text' && value === true);
        if (!anyTestRequired) {
          setMessage({ type: 'error', message: 'Toxikologie: Vyberte alespoň jedno požadované vyšetření.' });
          return;
        }
        const payload = {
          ...baseFields,
          toxicologist: toxicologistId,
          intoxication_suspicion: data.toxicology.intoxication_suspicion,
          circumstances_note: data.toxicology.circumstances_note,
          note: data.toxicology.note,
          form_data: data.toxicology.form_data
        };
        await api.labRequests.toxicology.patch(existingId, payload);
      } else if (apiType === 'tryptase') {
        const payload = { ...baseFields, do_request: data.tryptase.do_request };
        await api.labRequests.tryptase.patch(existingId, payload);
      } else if (apiType === 'microbiology') {
        if (!hasAnyBooleanTrue(data.microbiology.form_data)) {
          setMessage({ type: 'error', message: 'Mikrobiologie: Vyberte alespoň jednu položku.' });
          return;
        }
        const fd = data.microbiology.form_data;
        const sanitizedFormData = {
          ...fd,
          hcd: { ...fd.hcd, cultivation_type: fd.hcd.cultivation_type || null },
          dcd: { ...fd.dcd, cultivation_type: fd.dcd.cultivation_type || null },
          urogenital_sti: { ...fd.urogenital_sti, cultivation_type: fd.urogenital_sti.cultivation_type || null },
          ear: { ...fd.ear, side: fd.ear.side || null },
          eye: { ...fd.eye, side: fd.eye.side || null },
          bloodstream_bactec: { ...fd.bloodstream_bactec, collection_source: fd.bloodstream_bactec.collection_source || null }
        };
        const payload = { ...baseFields, note: data.microbiology.note, form_data: sanitizedFormData };
        await api.labRequests.microbiology.patch(existingId, payload);
      } else if (apiType === 'genetics') {
        const payload = { ...baseFields, pcr: data.genetics.pcr, medical: data.genetics.medical };
        await api.labRequests.genetics.patch(existingId, payload);
      } else if (apiType === 'biochemistry') {
        if (!hasAnyBooleanTrue(data.biochemistry.form_data)) {
          setMessage({ type: 'error', message: 'Biochemie: Vyberte alespoň jednu hodnotu.' });
          return;
        }
        const payload = {
          ...baseFields,
          primary_sample: data.biochemistry.primary_sample,
          form_data: data.biochemistry.form_data
        };
        await api.labRequests.biochemistry.patch(existingId, payload);
      } else if (apiType === 'histology') {
        const requestingPhysicianId = parseInt(data.histology.requesting_physician, 10);
        if (!data.histology.requesting_physician || isNaN(requestingPhysicianId)) {
          setMessage({ type: 'error', message: 'Indikující lékař je povinný.' });
          return;
        }
        if (!hasAnyBooleanTrue(data.histology.form_data.histology_fixed)) {
          setMessage({ type: 'error', message: 'Histologie: Vyberte alespoň jednu položku z Materiálu.' });
          return;
        }
        const payload = {
          ...baseFields,
          requesting_physician: requestingPhysicianId,
          macroscopic_heart_histochemistry: data.histology.macroscopic_heart_histochemistry,
          form_data: data.histology.form_data
        };
        await api.labRequests.histology.patch(existingId, payload);
      } else if (apiType === 'nrl_other') {
        const { ...nrlOtherData } = data.nrl_other;
        const payload = { ...baseFields, ...nrlOtherData };
        await api.labRequests.nrlOther.patch(existingId, payload);
      } else if (apiType === 'nrl_respiratory') {
        if (!hasNrlRespiratoryValidSelection(data.nrl_respiratory.form_data)) {
          setMessage({ type: 'error', message: 'NRL respirační: Vyplňte alespoň jednu položku v Materiálu nebo ve Vyšetření.' });
          return;
        }
        const toNumberOrNull = (value: unknown): number | null => {
          if (value === '' || value === null || value === undefined) return null;
          const num = Number(value);
          return Number.isNaN(num) ? null : num;
        };
        const sanitizedSymptoms = Object.entries(data.nrl_respiratory.form_data.symptoms).reduce(
          (acc, [key, symptom]) => {
            const s = symptom as any;
            acc[key] = {
              ...s,
              days: toNumberOrNull(s.days),
              ...('value_c' in s ? { value_c: toNumberOrNull(s.value_c) } : {})
            };
            return acc;
          },
          {} as Record<string, any>
        ) as typeof data.nrl_respiratory.form_data.symptoms;
        const payload = {
          ...baseFields,
          important_symptoms_note: data.nrl_respiratory.important_symptoms_note || null,
          focus_note: data.nrl_respiratory.focus_note || null,
          form_data: { ...data.nrl_respiratory.form_data, symptoms: sanitizedSymptoms }
        };
        await api.labRequests.nrlRespiratory.patch(existingId, payload);
      } else if (apiType === 'virology') {
        if (!hasAnyBooleanTrue(data.virology.form_data)) {
          setMessage({ type: 'error', message: 'Virologie: Vyberte alespoň jednu položku.' });
          return;
        }
        const payload = { ...baseFields, form_data: data.virology.form_data };
        await api.labRequests.virology.patch(existingId, payload);
      } else if (apiType === 'transport') {
        const requestingDepartmentId = parseInt(data.transport.requesting_department, 10);
        if (!data.transport.requesting_department || isNaN(requestingDepartmentId)) {
          setMessage({ type: 'error', message: 'Oddělení je povinné.' });
          return;
        }
        const payload = {
          autopsy_protocol: autopsyProtocolId,
          examination_type: examinationTypeId,
          requesting_department: requestingDepartmentId,
          form_data: data.transport.form_data,
          updated: true
        };
        await api.labRequests.transport.patch(existingId, payload);
      }

      setSavedComplements((prev) => new Set([...prev, type]));
      setDirtyComplements((prev) => {
        const next = new Set(prev);
        next.delete(type);
        return next;
      });
      setMessage({ type: 'success', message: 'Žádanka byla úspěšně uložena.' });
    } catch (error: any) {
      console.error(`Error saving ${type} request:`, error);
      setMessage({
        type: 'error',
        message: `Nepodařilo se uložit žádanku: ${extractErrorMessage(error, 'Neznámá chyba.')}`
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Watchers / effects
  // ---------------------------------------------------------------------------

  const togglePopup = () => setMessage(undefined);

  const complementOptions = useMemo(() => COMPLEMENT_OPTIONS, []);

  const selectedExamTypeId = methods.watch('examinationType');
  const selectedExamType = references.examinationTypes.find(
    (et) => String(et.id) === selectedExamTypeId
  );
  const isFnb = selectedExamType?.code === 'soudni';
  const selectedComplement = methods.watch('selectedComplement');
  const isOther = selectedComplement === 'other';

  const canPrint = selectedComplement !== null && savedComplements.has(selectedComplement);
  const canSend = selectedComplement !== null && savedComplements.has(selectedComplement);

  const [printing, setPrinting] = useState<boolean>(false);

  const onPrint = useCallback(async () => {
    const type = selectedComplement;
    if (!type || !(API_COMPLEMENT_TYPES as readonly string[]).includes(type)) return;
    const apiType = type as ApiComplementType;
    const id = requestIdsRef.current[apiType];
    if (id === null) return;

    try {
      setPrinting(true);
      const response = await (api.labRequests[API_KEY_MAP[apiType] as keyof typeof api.labRequests] as any).print(id);
      openPdfBlob(response);
    } catch {
      setMessage({ type: 'error', message: 'Tisk se nezdařil.' });
    } finally {
      setPrinting(false);
    }
  }, [api, selectedComplement]);

  const handleSend = useCallback(async () => {
    const type = selectedComplement;
    if (!type || !(API_COMPLEMENT_TYPES as readonly string[]).includes(type)) return;
    const apiType = type as ApiComplementType;
    const id = requestIdsRef.current[apiType];
    if (id === null) return;

    try {
      await (api.labRequests[API_KEY_MAP[apiType] as keyof typeof api.labRequests] as any)
        .patch(id, { sent_at: new Date().toISOString() });
      setMessage({ type: 'success', message: 'Žádanka byla odeslána.' });
    } catch {
      setMessage({ type: 'error', message: 'Odeslání se nezdařilo.' });
    }
  }, [api, selectedComplement, savedComplements]);

  useEffect(() => {
    if (!isOther) {
      methods.setValue('otherComplementNote', '');
      methods.clearErrors('otherComplementNote');
    }
  }, [isOther, methods]);

  useEffect(() => {
    if (!isFnb) {
      methods.setValue('fnbToxicologist', '');
      methods.clearErrors('fnbToxicologist');
    }
  }, [isFnb, methods]);

  const fields = {
    examinationType: {
      id: 'examinationType',
      name: 'examinationType',
      label: 'Typ vyšetření'
    },
    fnbToxicologist: {
      id: 'fnbToxicologist',
      name: 'fnbToxicologist',
      label: 'Zvolte toxikologa FNB'
    },
    otherComplementNote: {
      id: 'otherComplementNote',
      name: 'otherComplementNote',
      label: 'Upřesnění (ostatní)',
      rows: 2,
      required: false
    }
  };

  return {
    protocol,
    methods,
    fields,
    complementOptions,
    selectedComplement,
    savedComplements,
    dirtyComplements,
    currentUserName: currentUser?.name ?? '',
    message,
    togglePopup,
    onSubmit,
    canPrint,
    onPrint,
    printing,
    canSend,
    handleSend,
    isBatchLoading
  };
};
